import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import BarChart from "@/components/BarChart";
import FinalCTA from "@/components/FinalCTA";
import ScrollRail from "@/components/ScrollRail";
import { NumbersRail } from "@/components/Rails";
import LottiePlayer from "@/components/LottiePlayer";
import lineAnim from "@/assets/lottie/line-chart.json";

const STATS = [
  ["1.03B", "internet users in India, 70% of the population (Oct 2025)"],
  ["+223M", "new internet users in one year, up 27.7%"],
  ["500M", "social media user identities, 34.1% of the population"],
  ["4.0-4.4M", "active creators in India, with 3.3-3.7M on Instagram (2026)", 0, "Kofluence 2026"],
];

const STORIES = [
  ["Aman Gupta", "Co-founder, boAt", "Became one of India's most recognisable founder faces, and boAt's brand is inseparable from his public presence."],
  ["Kunal Shah", "Founder, CRED", "Built CRED's identity partly through his own sharp, public thinking. The founder's voice became part of the product's premium positioning."],
  ["Nish Hair", "Founder-led brand", "A brand where the founder's story and face carry the trust a faceless company would have to buy through ads."],
];

export default function Insights() {
  return (
    <>
      <Seo title="Why Now: India's Digital Market" description="Live market data on India's internet, social media and creator economy, and why personal brands and digital operations decide who wins." path="/insights" />
      <section className="hp-hero-light hp-immersive" style={{ minHeight: "75vh" }}>
        <img 
          src="/images/mumbai_skyline_bkc_1791103516730.jpg" 
          className="hp-immersive-img"
          style={{ objectPosition: 'center 40%' }}
          alt="Mumbai skyline" 
        />
        <div
          className="absolute inset-0 pointer-events-none bg-gradient-to-b lg:bg-gradient-to-r from-white via-white/95 to-white/10"
          aria-hidden="true"
        />
        <div className="hop-container relative z-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8"><Reveal>
            <p className="hp-label text-[var(--hop-brand)]">Why now</p>
            <h1 className="hp-h1 mt-5">The market moved online. Has your business?</h1>
            <p className="hp-lead mt-7">India added more internet users in a single year than most countries have people. Attention, trust and hiring all now happen on screens. Businesses that still operate the traditional way are competing against ones that don't.</p>
          </Reveal></div>
          <div className="hidden lg:block lg:col-span-4"><LottiePlayer className="hp-lottie-hero" data={lineAnim} label="Line chart climbing with milestone markers" /></div>
        </div>
      </section>
      <section className="hp-section hp-ink"><div className="hop-container">
        <SectionHead dark label="India by the numbers" title="A market this size does not wait." />
        <div className="mt-14">
          <ScrollRail inline={false} dark rail={(p) => <NumbersRail p={p} />}>
            {STATS.map(([n, d], i) => (
              <div key={n} className="hp-beat mb-8"><span className="hp-beat-n text-sky-300">0{i + 1}</span><div className="font-display text-5xl md:text-6xl text-white hp-stat-num mt-1">{n}</div><p className="mt-3 text-[17px] text-slate-300 max-w-md">{d}</p></div>
            ))}
          </ScrollRail>
        </div>
      </div></section>
      <section className="hp-section"><div className="hop-container">
        <SectionHead label="The data" title="Why personal brands and digital presence are booming." lead="Figures below are drawn from DataReportal, Kofluence and ISB research." />
        <div className="mt-12 grid lg:grid-cols-3 gap-6">
          <BarChart title="Online India, millions of people" max={1030} rows={[{ l: "Internet users", v: 1030, show: "1,030M" }, { l: "Social media identities", v: 500, show: "500M" }]} source="DataReportal Digital 2026" />
          <BarChart title="Creators in India, millions" max={4.4} rows={[{ l: "2020", v: 0.96, show: "0.96M" }, { l: "2025", v: 4.12, show: "4.12M" }, { l: "2026 (active, upper est.)", v: 4.4, show: "4.0-4.4M" }]} source="ISB SRITNE (2020, 2025); Kofluence 2026" />
          <BarChart title="Influencer marketing, ₹ crore (midpoints)" max={4750} rows={[{ l: "2025", v: 3250, show: "₹3,000-3,500 Cr" }, { l: "2027 (projected)", v: 4750, show: "₹4,500-5,000 Cr" }]} source="Kofluence 2026" />
        </div>
        <ul className="mt-10 grid md:grid-cols-3 gap-6 text-[15px] text-slate-700">
          <li className="border-t border-slate-300 pt-4"><b>22% CAGR.</b> India's influencer marketing sector is growing at about 22% a year from 2025.</li>
          <li className="border-t border-slate-300 pt-4"><b>15.2% of creators</b> are now registered businesses, so brands expect professional partners. <b>93.1% of brands</b> prioritise Instagram as their primary influencer channel.</li>
          <li className="border-t border-slate-300 pt-4"><b>~440M</b> Indians are still offline, so the next wave of growth is still ahead.</li>
        </ul>
      </div></section>
      <section className="hp-section relative overflow-hidden bg-[#f7f7f5]">
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} aria-hidden="true">
          <img
            src="/images/mumbai_station_commute_1791103539127.jpg"
            alt="Mumbai central station commute"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#f7f7f5] via-[#f7f7f5]/90 to-[#f7f7f5]/10" />
        </div>
        <div className="hop-container relative" style={{ zIndex: 10 }}>
          <SectionHead label="The pattern" title="Founders who became the brand." lead="Different industries, same move: the person and the product built trust together. Their brands are hard to copy because the story is theirs." />
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {STORIES.map(([n, r, d]) => (
              <article key={n} className="bg-white/40 backdrop-blur-md p-7 rounded-xl border border-slate-200/60 shadow-sm">
                <h3 className="hp-h3">{n}</h3>
                <p className="text-sm font-bold text-[var(--hop-brand)] mt-1">{r}</p>
                <p className="hp-body text-[15px] mt-4">{d}</p>
              </article>
            ))}
          </div>
          <p className="mt-8 hp-h3 max-w-2xl bg-white/40 backdrop-blur-md p-6 rounded-xl border border-slate-200/60 shadow-sm">We build that same engine for our clients, quietly. Under NDA, your personal brand is managed by us and nobody needs to know.</p>
        </div>
      </section>
      <section className="hp-section"><div className="hop-container">
        <SectionHead label="Our output" title="50+ content pieces every month." lead="Across our client base we write, script and edit more than 50 pieces of content each month. Client names stay private under NDA." />
      </div></section>
      <FinalCTA />
    </>
  );
}
